import React from 'react';

import type { SurfaceLevel, SurfaceProps } from './Surface.types';

/**
 * Maps surface level to background color class.
 */
const LEVEL_CLASSES: Record<SurfaceLevel, string> = {
  void: 'bg-contrast-950',
  base: 'bg-contrast-700',
  elevated: 'bg-contrast-600',
};

/**
 * Maps padding size to Tailwind padding class.
 */
const PADDING_CLASSES: Record<NonNullable<SurfaceProps['padding']>, string> = {
  none: '',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-8',
};

/**
 * Maps rounded size to Tailwind border-radius class.
 */
const ROUNDED_CLASSES: Record<NonNullable<SurfaceProps['rounded']>, string> = {
  none: '',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
};

/**
 * Surface container primitive for consistent elevation across the app.
 *
 * Encapsulates the Dō Sakura design system surface depth tokens,
 * providing a three-tier elevation system that creates visual depth
 * on dark backgrounds.
 *
 * @example
 * ```tsx
 * // Card container
 * <Surface level="base" padding="lg" rounded="lg">
 *   Card content
 * </Surface>
 *
 * // Modal container with border
 * <Surface level="elevated" padding="xl" rounded="xl" border>
 *   Modal content
 * </Surface>
 *
 * // Section with semantic HTML
 * <Surface as="section" level="base" padding="lg">
 *   Section content
 * </Surface>
 * ```
 */
export function Surface({
  level = 'base',
  as: Component = 'div',
  padding = 'none',
  rounded = 'none',
  border = false,
  className = '',
  children,
}: SurfaceProps): React.JSX.Element {
  const levelClass = LEVEL_CLASSES[level];
  const paddingClass = PADDING_CLASSES[padding];
  const roundedClass = ROUNDED_CLASSES[rounded];
  const borderClass = border ? 'border border-stroke-default' : '';

  const classes = [
    levelClass,
    paddingClass,
    roundedClass,
    borderClass,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <Component className={classes}>{children}</Component>;
}

export default Surface;
